import type * as React from "react";

import { cx } from "../../lib/cx";
import { Icon } from "../core/Icon";
import s from "./Pagination.module.css";

export interface PaginationProps extends Omit<React.ComponentPropsWithoutRef<"nav">, "onChange"> {
  /** Página actual, desde 1. */
  page: number;
  /** Cantidad total de páginas. */
  total: number;
  /** Paginado por URL: la página N es un enlace. */
  hrefFor?: (page: number) => string;
  /** Paginado por estado (sólo desde un client component). */
  onChange?: (page: number) => void;
  /** El componente del enlace, p. ej. `Link` de Next. */
  linkAs?: React.ElementType;
  /** Qué se pagina, para el lector de pantalla. */
  label?: string;
  /** Cuántas páginas se muestran a cada lado de la actual antes de los puntos suspensivos. */
  siblings?: number;
}

/** Los números a mostrar, con `null` donde van los puntos suspensivos: 1 … 4 5 6 … 12. */
export function paginasVisibles(page: number, total: number, siblings = 1): Array<number | null> {
  const desde = Math.max(2, page - siblings);
  const hasta = Math.min(total - 1, page + siblings);
  const medio: number[] = [];
  for (let p = desde; p <= hasta; p++) medio.push(p);
  const lista: Array<number | null> = [1];
  if (desde > 2) lista.push(null);
  lista.push(...medio);
  if (hasta < total - 1) lista.push(null);
  if (total > 1) lista.push(total);
  return lista;
}

/** Anterior, los números y siguiente. Por URL (`hrefFor`) o por estado (`onChange`). Con una sola página no dibuja nada. */
export function Pagination({
  page,
  total,
  hrefFor,
  onChange,
  linkAs,
  label = "Páginas",
  siblings = 1,
  className,
  ...rest
}: PaginationProps) {
  if (total <= 1) return null;
  const LinkTag: React.ElementType = linkAs ?? "a";

  function Pagina({ destino, activo = true, actual = false, flecha, children }: { destino: number; activo?: boolean; actual?: boolean; flecha?: boolean; children: React.ReactNode }) {
    const comunes = { className: s.pagina, "data-flecha": flecha || undefined, "aria-current": actual ? ("page" as const) : undefined };
    if (hrefFor && !actual) {
      return (
        <LinkTag href={activo ? hrefFor(destino) : undefined} aria-disabled={!activo || undefined} {...comunes}>
          {children}
        </LinkTag>
      );
    }
    return (
      <button type="button" disabled={!activo} onClick={() => !actual && onChange?.(destino)} {...comunes}>
        {children}
      </button>
    );
  }

  return (
    <nav className={cx(s.nav, className)} aria-label={label} {...rest}>
      <Pagina destino={page - 1} activo={page > 1} flecha>
        <span className={s.estado}>Anterior</span>
        <Icon name="chevron-left" size={14} />
      </Pagina>
      {paginasVisibles(page, total, siblings).map((p, i) =>
        p === null ? (
          <span key={`puntos-${i}`} className={s.puntos} aria-hidden="true">
            …
          </span>
        ) : (
          <Pagina key={p} destino={p} actual={p === page}>
            {p}
          </Pagina>
        ),
      )}
      <Pagina destino={page + 1} activo={page < total} flecha>
        <span className={s.estado}>Siguiente</span>
        <Icon name="chevron-right" size={14} />
      </Pagina>
      <span className={s.estado} aria-live="polite">
        Página {page} de {total}
      </span>
    </nav>
  );
}
